import { useEffect, useMemo, useState } from 'react'
import * as Sentry from '@sentry/react'
import { toast } from 'sonner'
import { setScraperProxyPort, type StaffOrg } from '../services/staff'
import { PROXY_PORT_MAX, PROXY_PORT_MIN, isValidProxyPort } from '../../shared/proxyPort'

interface ScraperProxyPortProps {
  /** Org list, already loaded by StaffConsole (may be null while loading). */
  orgs: StaffOrg[] | null
  loadError: string | null
  /** The row's stored port after a save, so the console's list stays true. */
  onSaved: (orgId: string, proxyPort: number | null) => void
}

const RENDER_CAP = 50

/**
 * One practice's dedicated egress address: one Oxylabs gateway port, i.e. one
 * fixed IP every portal login of this practice leaves from — Revolution, VSP
 * and EyeMed, every clinic of the practice — and the condition for the
 * practice to scrape at all. Own draft + save like SmsManagement's NumberRow;
 * the row shows whatever the server echoes back (nothing optimistic). A
 * practice with a configured integration and no port reads "Not scraping —
 * no address assigned". Saving a value is one click; clearing asks first,
 * because nothing of the practice runs until a port is assigned again — the
 * queued work included.
 */
function ProxyPortRow({ org, onSaved }: {
  org: StaffOrg
  onSaved: ScraperProxyPortProps['onSaved']
}) {
  const stored = org.proxyPort === null ? '' : String(org.proxyPort)
  const [draft, setDraft] = useState(stored)
  const [saving, setSaving] = useState(false)
  const [confirmClear, setConfirmClear] = useState(false)
  useEffect(() => { setDraft(stored) }, [stored])
  const trimmed = draft.trim()
  const changed = trimmed !== stored
  const notScraping = org.proxyPort === null && org.scraperConfigured

  async function save(port: number | null) {
    setSaving(true)
    try {
      const res = await setScraperProxyPort(org.orgId, port)
      onSaved(org.orgId, res.proxyPort)
      setDraft(res.proxyPort === null ? '' : String(res.proxyPort))
      toast.success(res.proxyPort === null
        ? `${org.name}: dedicated proxy cleared`
        : `${org.name}: dedicated proxy port ${res.proxyPort} saved`)
    } catch (err) {
      Sentry.captureException(err)
      toast.error(err instanceof Error ? err.message : 'Could not save the proxy port.')
    } finally {
      setSaving(false)
    }
  }

  function submit() {
    if (trimmed === '') {
      // Clearing asks first — the practice stops scraping until a port is
      // assigned again. Nothing is written until the operator confirms.
      setConfirmClear(true)
      return
    }
    const port = Number(trimmed)
    if (!isValidProxyPort(port)) {
      toast.error('Enter a whole port number from 1 to 65535, or leave it empty to clear.')
      return
    }
    void save(port)
  }

  return (
    <li className="staff-org-row">
      <div className="staff-org-meta">
        <span className="staff-org-name">{org.name}</span>
        <span className="staff-org-id">{org.orgId}</span>
        {notScraping && (
          <span className="staff-org-warning" data-testid="not-scraping">
            Not scraping — no address assigned
          </span>
        )}
      </div>
      <div className="staff-org-actions">
        <span className="sms-mgmt-routes__label">Dedicated proxy port:</span>
        <input
          className="sms-mgmt-row__input"
          style={{ width: '6rem' }}
          type="number"
          inputMode="numeric"
          min={PROXY_PORT_MIN}
          max={PROXY_PORT_MAX}
          placeholder="8001"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={saving}
          aria-label={`Dedicated proxy port for ${org.name}`}
        />
        <button className="staff-view-button" onClick={submit} disabled={saving || !changed}>
          {saving ? 'Saving…' : trimmed ? 'Save' : 'Clear'}
        </button>
      </div>
      {confirmClear && (
        <div className="doctor-modal-overlay" onClick={() => setConfirmClear(false)}>
          <div
            className="doctor-modal pab-confirm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="proxy-clear-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="doctor-modal__header">
              <h3 className="doctor-modal__title" id="proxy-clear-title">Clear this practice&rsquo;s address?</h3>
              <button className="doctor-modal__close" onClick={() => setConfirmClear(false)} aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="add-doctor-form">
              <p className="pab-confirm__text" data-testid="confirm-text">
                {`Every pull and check for ${org.name} fails until a port is assigned, the queued ones included.`}
              </p>
              <div className="add-doctor-form__actions">
                <span />
                <div className="add-doctor-form__actions-right">
                  <button className="add-doctor-form__cancel" onClick={() => setConfirmClear(false)}>Cancel</button>
                  <button
                    className="add-doctor-form__delete"
                    data-testid="confirm-ok"
                    onClick={() => {
                      setConfirmClear(false)
                      void save(null)
                    }}
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </li>
  )
}

/**
 * Scraper Proxy — the staff section that assigns each practice's dedicated
 * egress port, or clears it. All that remains of the engine page: every
 * vendor's work goes to that vendor's scraper lane, and there is no choice of
 * engine anywhere. The port is read off the console's org row and saved
 * through `setScraperProxyPort` (staff-only, audited); a port another practice
 * holds is refused by the backend.
 */
export default function ScraperProxyPort({ orgs, loadError, onSaved }: ScraperProxyPortProps) {
  const [filter, setFilter] = useState('')

  const filtered = useMemo(() => {
    if (!orgs) return []
    const q = filter.trim().toLowerCase()
    const list = q
      ? orgs.filter((o) => o.name.toLowerCase().includes(q) || o.orgId.toLowerCase().includes(q))
      : orgs
    return list.slice(0, RENDER_CAP)
  }, [orgs, filter])

  return (
    <div className="staff-emulator">
      <header className="staff-emulator__head">
        <div>
          <h1>Scraper Proxy</h1>
          <p className="staff-console-subtitle">
            Each practice&rsquo;s dedicated egress address — the fixed address every portal login
            of the practice leaves from: Revolution, VSP and EyeMed. A practice scrapes only from
            its own address, so a port is required before its first pull or check; with none, every
            pull and check for the practice is skipped, refused or flagged until one is assigned.
          </p>
        </div>
      </header>
      <div className="staff-console-toolbar">
        <input
          type="search"
          placeholder="Search practices by name or ID…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          aria-label="Search practices"
        />
      </div>
      {loadError && <p className="auth-error">{loadError}</p>}
      {!orgs && !loadError && <p className="staff-console-loading">Loading organizations…</p>}
      {orgs && (
        <ul className="staff-org-list">
          {filtered.map((org) => (
            <ProxyPortRow key={org.orgId} org={org} onSaved={onSaved} />
          ))}
          {filtered.length === 0 && <li className="staff-org-empty">No practices match “{filter}”.</li>}
        </ul>
      )}
    </div>
  )
}
